import { useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";

import { CountryPickerSheet } from "./country-picker-sheet";

/**
 * Routes that carry their own context (a token, a provider, a kiosk) and
 * never read the browsing country, so the blocking picker must not cover
 * them. They are opened from email/SMS links, embedded in a clinic's own
 * site (`/widget` lives in a third-party iframe where the SameSite=Lax
 * country cookie can be neither read nor written) or shown on fresh kiosk
 * screens. Matched as path prefixes.
 */
const GATE_EXEMPT_PREFIXES = [
  "/display",
  "/widget/",
  "/auth/",
  "/appointments/confirm/",
  "/requests/",
  "/waiting-room/",
  "/book/",
  "/legal",
  "/get-app",
] as const;

function isGateExempt(pathname: string): boolean {
  return GATE_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Boot-time gate. Renders the underlying app once the cookie/header path
 * resolves a country; otherwise overlays a blocking `CountryPickerSheet`
 * over a dimmed shell so first-time users can't navigate around without
 * picking a country.
 *
 * - `isHydrated === false` → render nothing (the index.html splash stays
 *   visible until React mounts; this matches the existing `#root:empty`
 *   pulse).
 * - `country === null` (cookie missing AND CF Pages didn't inject one,
 *   e.g. localhost or a non-CF preview) → blocking sheet opens, except on
 *   `GATE_EXEMPT_PREFIXES` and while a legal document is open (`?legal=`,
 *   from a `/legal/<segment>` link) — the picker comes back once the
 *   document is closed.
 * - `country` set → children render normally.
 *
 * The header switcher uses the same component in `dismissible` mode and
 * lives in `app-header.tsx` (P7).
 */
/**
 * True while the blocking country picker is (or is about to be) on screen.
 * Other boot gates (the auth modal in `_authenticated`) wait for it, so the
 * two never stack and the user isn't asked for a country twice (P4).
 */
export function useCountryGateBlocking(): boolean {
  const { country, isHydrated } = useBrowsingCountry();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const legalOpen = useRouterState({
    select: (s) => typeof (s.location.search as Record<string, unknown>).legal === "string",
  });
  if (!isHydrated) return true;
  return !country && !isGateExempt(pathname) && !legalOpen;
}

export function CountryGate({ children }: { children: ReactNode }) {
  const { isHydrated, setCountry } = useBrowsingCountry();
  const blocking = useCountryGateBlocking();

  if (!isHydrated) return null;

  if (blocking) {
    return (
      <>
        {/* Dimmed shell behind the blocking sheet — provides visual context
            (header, skeleton hints) without being interactive. */}
        <div className="pointer-events-none opacity-30">{children}</div>
        <CountryPickerSheet open mode="blocking" current={null} onSelect={(c) => setCountry(c)} />
      </>
    );
  }

  return <>{children}</>;
}
